<template>
  <tr
    @mouseover="$emit('changeHoveredId', props._key)"
    @mouseout="$emit('changeHoveredId', '')"
  >
    <td class="sc-data-list-select">
      <v-list-item class="ml-0 pl-0 pr-0">
        <v-list-item-action
          v-if="allowSelect"
          class="ml-0 mr-4"
        >
          <v-checkbox
            primary
            hide-details
            :input-value="selected"
            @change="$emit('toggleItem', props)"
          />
        </v-list-item-action>
        <v-chip
          small
          label
          class="sc-nbr"
          :class="getNBRPropertyTypeClass(props.property ? props.property.type : '')"
          dark
        >
          <span>{{ props.number }}</span>
        </v-chip>
      </v-list-item>
    </td>
    <td v-if="allowPhoto" class="sc-data-list-property-photo">
      <router-link
        :to="getAccountRoute({ name: 'comparable', params: { id: props._key }})"
        class="property-name"
      >
        <Photo
          :photo-url="getPhotoUrl"
          photo-class="comparable-photo"
          :transaction-type-label="getComparableTransactionImageLabel(props.transaction)"
        />
      </router-link>
    </td>
    <td class="sc-data-list-property-address property-address">
      <div v-if="!props.active">
        <v-chip
          x-small
          color="black"
          class="lighten-3 mb-1"
          dark
        >
          Inactive
        </v-chip>
      </div>
      <div class="record-id">Comparable ID# {{ props._key }}</div>
      <span v-if="allowLink">
        <router-link
          v-if="!props.working"
          :to="getAccountRoute({ name: 'comparable', params: { id: props._key }})"
          class="property-name"
        >
          <div v-if="props.name"><strong>{{ props.name }}</strong></div>
          <div>{{ props.address.street }}<span v-if="props.address.street2">, {{ props.address.street2 }}</span></div>
          <div>{{ props.address.city }}<span v-if="props.address.state || props.address.postal">, {{ props.address.state }} {{ props.address.postal }}</span></div>
        </router-link>
        <router-link
          v-else
          :to="getAccountRoute({ name: 'comparable-create-id', params: { id: props._key }})"
          class="property-name"
        >
          <div v-if="props.name"><strong>{{ props.name }}</strong></div>
          <div>{{ props.address.street }}<span v-if="props.address.street2">, {{ props.address.street2 }}</span></div>
          <div>{{ props.address.city }}<span v-if="props.address.state || props.address.postal">, {{ props.address.state }} {{ props.address.postal }}</span></div>
        </router-link>
        <div v-if="props.confidential" class="mt-2">
          <ConfidentialChip />
        </div>
      </span>
      <span v-else class="property-name">
        <span
          v-if="props.name"
        ><strong>{{ props.name }}</strong></span><br>
        {{ props.address.street }}<br>{{ props.address.city }},
        {{ props.address.state }} {{ props.address.postal }}
      </span>
      <AvailabilityChipsWrapper
        :type="props.property_type"
        :mixed-use="props.mixed_use"
        :in-house="props.in_house"
        :is-portfolio="props.is_portfolio"
      />
    </td>
    <td class="sc-data-list-property-data">
      <v-chip
        v-if="props.working"
        x-small
        color="orange accent-4"
        dark
      >
        working
      </v-chip>
      <div
        v-if="
          props.transaction_type === 'lease' ||
            props.transaction_type === 'sale'
        "
        class="transaction-type"
      >
        <span>{{ transactionTypeLabel }}</span>
      </div>
      <div v-else class="na">
        N/A
      </div>
      <div class="property-type">
        <span v-if="props.property_type">{{ props.property_type }}</span>
        <span v-else class="na">N/A</span>
      </div>
      <div class="property-units">
        <span v-if="props.details.building.units">{{ props.details.building.units }}</span>
        <span v-else class="na">N/A</span>
      </div>
    </td>
    <td class="sc-data-list-property-markets">
      <div class="market"><Label :value="props.primary_use" /></div>
      <div class="market">
        <span v-if="props.filterMarketOne">{{ props.filterMarketOne }}</span>
        <span v-else-if="user.active_account.id === 6">Out of Area</span>
        <span v-else class="na">N/A</span>
      </div>
      <div v-if="marketColumns.two" class="market">
        <span v-if="props.filterMarketTwo">{{ props.filterMarketTwo }}</span>
        <span v-else class="na">N/A</span>
      </div>
    </td>
    <td class="sc-data-list-property-data">
      <div>
        <BuildingSizeLabel :value="props.transaction.total_square_feet" />
      </div>
      <div v-if="props.property">
        <BuildingSizeLabel :value="props.property.building_size" />
      </div>
      <div v-else>
        <span class="na">n/a</span>
      </div>
      <div>
        <template v-if="props.land_size">
          <LandSizeLabel :value="props.land_size" />
          <POLIcon v-if="props.transaction.land_pol" />
        </template>
        <span v-else class="na">N/A</span>
      </div>
    </td>
    <td class="sc-data-list-property-data sale-details">
      <div>
        <ComparableSalesPriceLabel
          v-if="props.transaction_type === 'sale'"
          label="Sales Price"
          :property-type="props.property ? props.property.type : ''"
          :total-acres="props.transaction.total_acres"
          :total-price="props.transaction.total_price"
          :total-price-per="props.transaction.total_price_per"
          hide-label
        />
        <Label v-else />
      </div>
      <div>
        <Label :value="props.transaction.cap_rate ? $options.filters.formatPercent(props.transaction.cap_rate) : null" />
      </div>
    </td>
    <td class="sc-data-list-property-data lease-details">
      <div v-if="props.transaction_type === 'lease'">
        <div>
          {{ props.starting_rent_sf | formatCurrency }} {{ props.lease_type }}
          <span v-if="props.transaction.escalation">({{ props.transaction.escalation | formatPercent }})</span>
        </div>
        <div>
          {{ props.effective_rent_sf | formatCurrency }} {{ props.lease_type }}
        </div>
        <div>
          <LeaseCommencementExpirationLabel :lease-commencement="props.lease_commencement" :lease-expiration="props.lease_expiration" /> <br>
          ({{ leaseTermLength }})
        </div>
      </div>
      <template v-else>
        <Label /> <br>
        <Label /> <br>
        <Label />
      </template>
    </td>
    <td class="sc-data-list-property-data">
      <div>
        <span
          v-if="props.tenant_improvements.landlord.persf > 0"
        >{{ props.tenant_improvements.landlord.persf | formatCurrency }} /
          SF</span>
        <span v-else class="na">N/A</span>
      </div>
      <div>
        <Label :value="freeRentLength" />
      </div>
      <div>
        <span v-if="props.transaction_date">
          {{ props.transaction_date | formatDate }}
          {{ monthsOnMarket ? `(${monthsOnMarket})` : '' }}
        </span>
        <span v-else class="na">n/a</span>
      </div>
    </td>
    <td class="sc-data-list-property-contacts contacts">
      <div v-if="props.transaction_type === 'sale'">
        <strong v-if="props.companies && props.companies.buyer">
          {{ props.companies.buyer.name }}
        </strong>
        <Label v-else /> <br>
        <div v-for="contact in props.contacts.buyer" :key="contact._key">
          {{ contact.first_name }} {{ contact.last_name }}
        </div>
        <Label v-if="!props.contacts.buyer.length" />
      </div>

      <div v-else-if="props.transaction_type === 'lease'">
        <strong v-if="props.companies && props.companies.tenant">{{
          props.companies.tenant.name
        }}</strong>
        <Label v-else /> <br>
        <div v-for="contact in props.contacts.tenant" :key="contact._key">
          {{ contact.first_name }} {{ contact.last_name }}
        </div>
        <Label v-if="!props.contacts.tenant.length" />
      </div>

      <template v-else>
        <Label /> <br>
        <Label />
      </template>
    </td>
    <td class="sc-data-list-property-contacts contacts">
      <div v-if="props.transaction_type === 'sale'">
        <strong v-if="props.companies && props.companies.seller">{{
          props.companies.seller.name
        }}</strong>
        <Label v-else />
        <div v-for="contact in props.contacts.seller" :key="contact._key">
          {{ contact.first_name }} {{ contact.last_name }}
        </div>
      </div>

      <div v-if="props.transaction_type === 'lease'">
        <div>
          <strong v-if="props.companies && props.companies.landlord">{{ props.companies.landlord.name }}</strong>
          <strong v-else-if="props.contacts.landlord">{{ landlordContactCompanyName }}</strong>
          <Label v-else />
        </div>
        <div v-if="hasLandlordContacts">
          <div v-for="contact in props.contacts.landlord" :key="contact._key">
            {{ contact.first_name }} {{ contact.last_name }}
          </div>
        </div>
        <div v-else>
          <Label />
        </div>
      </div>
    </td>
    <td class="sc-data-list-property-contacts contacts">
      <div
        v-if="props.contacts.seller_agent.length || props.contacts.landlord_agent.length"
        class="seller-landlord-agents"
      >
        <span v-if="props.contacts.seller_agent.length">
          {{ props.contacts.seller_agent[0].name }}{{ props.contacts.seller_agent[0].company ? `, ${props.contacts.seller_agent[0].company.name}`: '' }}
        </span>
        <span v-if="props.contacts.landlord_agent.length">
          {{ props.contacts.landlord_agent[0].name }}{{ props.contacts.landlord_agent[0].company ? `, ${props.contacts.landlord_agent[0].company.name}` : '' }}
        </span>
      </div>
      <div v-else><span class="na">n/a</span></div>
      <div
        v-if="props.contacts.buyer_agent.length || props.contacts.tenant_agent.length"
        class="buyer-tenant-agents"
      >
        <span v-if="props.contacts.buyer_agent.length">
          {{ props.contacts.buyer_agent[0].name }}{{ props.contacts.buyer_agent[0].company ? `, ${props.contacts.buyer_agent[0].company.name}` : '' }}
        </span>
        <span v-if="props.contacts.tenant_agent.length">
          {{ props.contacts.tenant_agent[0].name }}{{ props.contacts.tenant_agent[0].company ? `, ${props.contacts.tenant_agent[0].company.name}` : '' }}
        </span>
      </div>
      <div v-else><span class="na">n/a</span></div>
    </td>
    <td class="sc-data-list-updated-created">
      <div v-if="props.created" class="updated-date">
        {{ props.updated.date | formatJSDateTime }}, {{ props.updated.name }}
      </div>
      <div v-if="props.created" class="created-date">
        {{ props.created.date | formatJSDateTime }}, {{ props.created.name }}
      </div>
      <div v-if="props.contacts.source && props.contacts.source.length">
        <div v-for="contact in props.contacts.source" :key="contact._key">
          <span v-if="contact.name">{{ contact.name }}</span>
          <span v-else>{{ contact.first_name }} {{ contact.last_name }}</span>
        </div>
      </div>
      <div v-if="props.legacy_id" style="font-size: .8em; margin-top: 5px;">Legacy ID: {{ props.legacy_id }}</div>
      <div v-if="customField">
        <Label :value="getCustomFieldValue(props, customField)" />
      </div>
    </td>
  </tr>
</template>

<script lang="ts">
import { mapState } from 'vuex';
import mixins from 'vue-typed-mixins';

import { getMarketColumns, IMarketColumns } from '@/bundles/Properties/helpers/marketColumns';
import { AccountLayoutMixin } from '@/bundles/App/mixins/accountMixin';
import {
  getComparableTransactionImageLabel,
  getComparableTransactionTypeLabelWithSchedule
} from '@/bundles/Comparables/helpers/comparableTransaction';
import { getComparableFreeRentLength, getComparableLeaseTermLength } from '@/bundles/Comparables/helpers';
import { getNBRPropertyTypeClass } from '@/bundles/Property/helpers/colors/getNBRPropertyTypeClass';
import { getDiffBetweenDates } from '@/utils/filters';
import { roundToDecimal } from '@/bundles/App/helpers/mathHelpers';
import { isValuePresent } from '@/bundles/App/helpers/isValuePresent';
import { getCustomFieldValue } from '@/bundles/Comparables/helpers/comparableCustomFieldMapper';

import Photo from '@/components/common-components/photo/Photo.vue';
import LandSizeLabel from '@/bundles/MeasurementUnits/components/LandSizeLabel.vue';
import BuildingSizeLabel from '@/bundles/MeasurementUnits/components/BuildingSizeLabel.vue';
import POLIcon from '@/bundles/Comparables/components/POLIcon.vue';
import CommonChip from '@/bundles/Common/components/chips/CommonChip.vue';
import Label from '@/bundles/Common/components/Label.vue';
import AvailabilityChipsWrapper from '@/bundles/Availabilities/components/chips/AvailabilityChipsWrapper.vue';
import ComparableSalesPriceLabel from '@/bundles/Comparables/components/labels/ComparableSalesPriceLabel.vue';
import ConfidentialChip from '@/bundles/Comparables/components/confidential/ConfidentialChip.vue';
import LeaseCommencementExpirationLabel from '@/bundles/Common/components/LeaseCommencementExpirationLabel.vue';

export default mixins(AccountLayoutMixin).extend({
  name: 'ComparablesListTableContent',

  components: {
    ConfidentialChip,
    ComparableSalesPriceLabel,
    AvailabilityChipsWrapper,
    Label,
    POLIcon,
    Photo,
    LandSizeLabel,
    BuildingSizeLabel,
    CommonChip,
    LeaseCommencementExpirationLabel
  },

  props: {
    props: {
      type: Object,
      required: true,
    },
    selected: Boolean,
    allowSelect: Boolean,
    allowLink: {
      type: Boolean,
      default: () => true,
    },
    allowPhoto: {
      type: Boolean,
      default: () => true,
    },
    customField: {
      type: String,
      default: null
    },
    selectOne: Boolean
  },

  computed: {
    ...mapState({
      user: (state: any) => state.user,
      marketColumns: (state: any): IMarketColumns => getMarketColumns(state.user.active_account.id, state.accountSettings, 'comparable'),
    }),
    getPhotoUrl: function () {
      return this.props.photo_links?.thumbnail_url || this.props.photo?.url;
    },
    transactionTypeLabel () {
      return getComparableTransactionTypeLabelWithSchedule({
        transactionType: this.props.transaction.transaction_type,
        investmentSale: this.props.transaction.investment_sale,
        sublease: this.props.transaction.sublease,
        scheduleType: this.props.transaction.schedule_type,
      });
    },
    leaseTermLength () {
      return getComparableLeaseTermLength(this.props);
    },
    freeRentLength () {
      return getComparableFreeRentLength(this.props);
    },
    hasLandlordContacts () {
      return this.props.contacts?.landlord?.length || false;
    },
    landlordContactCompanyName () {
      return this.props.contacts?.landlord[0]?.company?.name || null;
    },
    monthsOnMarket () {
      if (!this.props.transaction.transaction_date || !this.props.transaction.date_on_market) {
        return null;
      }

      const days = getDiffBetweenDates(this.props.transaction.date_on_market, this.props.transaction.transaction_date, ['days']).days;

      if (!days || days < 0) {
        return null;
      }

      return days < 30 ? roundToDecimal(days / 30, 1) : Math.round(days / 30);
    },
  },

  methods: {
    getComparableTransactionImageLabel,
    getNBRPropertyTypeClass,
    isValuePresent,
    getCustomFieldValue,
    isContact: function (contactId) : boolean {
      return contactId.substring(0, 7) === 'Contact'
    },
  },
});
</script>
